import React, { useState, useEffect } from 'react';
import {
  TextField,
  InputAdornment,
  Box,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { withDevice, WithDeviceProps } from '../hooks/withDevice';
import { withNavigation, WithNavigationProps } from '../hooks/withNavigation';
import { useLatestProjects } from '../hooks/useLatestProjects';
import { useSearchProjects } from '../hooks/useSearchProjects';
import ProjectList from '../components/ProjectList';
import { Project } from '../types';
import { Loading } from '../components/Loading';

interface HomeProps extends WithDeviceProps, WithNavigationProps {}

const Home: React.FC<HomeProps> = ({ isMobileDevice, navigate }) => {
  const { userData, loading: authLoading } = useAuth();
  const { t } = useTranslation();
  
  // State management
  const [searchQuery, setSearchQuery] = useState('');
  const {projects, loading, error, refetch, preQuery, totalFound, clearResults} = useSearchProjects();
  const latestProjects = useLatestProjects( { limit: isMobileDevice ? 5 : 12 } );
  const [filterProjects, setFilterProjects] = useState<Project[] | null>(null);
  const [loadFilterProjects, setLoadFilterProjects] = useState(true);
  const [newSearch, setNewSearch] = useState(false);
  const [viewClearButton, setViewClearButton] = useState(false);

  // Whenever a project is set or latestprojects set, filter the projects
  useEffect(() => {
    if (projects && projects.length > 0 && !loading) {
      // set load filter projects  to false when projects is set
      setFilterProjects(projects);
    } else if (preQuery !== '' && !loading) {
      setFilterProjects([]);
    } else if (latestProjects.projects.length > 0 && !loading) {
      setFilterProjects(latestProjects.projects);
    }
  }, [projects, latestProjects.projects, preQuery, loading]);

  useEffect(() => {
    if (filterProjects) {
      setLoadFilterProjects(false);
    }
  }, [filterProjects]);


  // Handle search input change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchQuery(value);
  };

  // Handle clear search
  const handleClearSearch = () => {
    setSearchQuery('');
    clearResults();
    setFilterProjects(null);
    setLoadFilterProjects(true);
  };
  

  useEffect(() => {
    setNewSearch(true);
  }, [searchQuery]);

  // Handle search input key press
  const handleSearchKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && newSearch) {
      setLoadFilterProjects(true);
      refetch(searchQuery);
      setNewSearch(false);
      setFilterProjects(null);
      setLoadFilterProjects(true);
      setViewClearButton(true);
    }
  };
  
  // Handle project click
  const handleProjectClick = (project: Project) => {
    navigate(`/project/${encodeURIComponent(project.project_code)}`);
  };
  
  // Show loading screen when user is not yet identified
  if (authLoading || !userData) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          bgcolor: 'background.default',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box
      sx={{
        bgcolor: 'background.default',
        pb: 0,
        pt: 1,
        pl: 1,
        pr: 1,
      }}
    >
      {/* Sticky AppBar */}
      <Card sx={{ mt: 2, ml: 2, mr: 2 }}>
        <CardContent sx={{ pt: 3 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder={t('home.projects.searchPlaceholder')}
            value={searchQuery}
            onChange={handleSearchChange}
            onKeyPress={handleSearchKeyPress}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleClearSearch}
                    edge="end"
                    size="small"
                    sx={{ 
                      padding: '4px',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      }
                    }}
                  >
                    {viewClearButton && <ClearIcon fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: isMobileDevice ? '16px' : '14px', // Prevents zoom on iOS
                minHeight: isMobileDevice ? '48px' : '40px',
              },
            }}
          />
        </CardContent>
      </Card>
      
      {/* Project list */}
      {filterProjects && (  
      <ProjectList
        projects={filterProjects}
        loading={loading || latestProjects.loading || loadFilterProjects}
        error={error || latestProjects.error}
        onProjectClick={handleProjectClick}
        searchQuery={preQuery}
        isMobileDevice={isMobileDevice}
      />
      )}
      {filterProjects === null && (loading || latestProjects.loading || loadFilterProjects) && (
        <Loading />
      )}
    </Box>
  );
};

export default withDevice(withNavigation(Home));